<template>
  <div class="portal-settings-wrapper" v-if="isPortalModuleReady">
    <form data-vv-scope="edit-device" class="portal-settings-form">
      <!-- Main Content Grid -->
      <div class="vx-row" style="margin-left: -0.5rem; margin-right: -0.5rem;">
        <!-- LEFT: General Settings -->
        <div class="vx-col w-full md:w-1/2 mb-3" style="padding-left: 0.5rem; padding-right: 0.5rem;">
          <vx-card class="settings-card">
            <div class="card-header-custom">
              <div class="card-title-custom">
                <feather-icon icon="SettingsIcon" svgClasses="w-5 h-5 text-primary mr-2" />
                <h4>General Settings</h4>
              </div>
            </div>

            <div class="settings-list" v-if="isMounted && portalConfig && Object.keys(portalConfig).length > 0">
              <div class="setting-row">
                <label class="setting-label">
                  <feather-icon icon="UserIcon" svgClasses="w-4 h-4 text-primary mr-2" />
                  Ask Customer Name
                </label>
                <vs-switch v-model="configName" color="success" />
              </div>

              <div class="setting-row">
                <label class="setting-label">
                  <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4 text-primary mr-2" />
                  Ask Destination
                </label>
                <vs-switch v-model="configDestination" color="success" />
              </div>

              <div class="setting-row">
                <label class="setting-label">
                  <feather-icon icon="PhoneIcon" svgClasses="w-4 h-4 text-primary mr-2" />
                  Ask Phone Number
                </label>
                <vs-switch v-model="configPhone" color="success" />
              </div>

              <div class="setting-row">
                <label class="setting-label">
                  <feather-icon icon="CalendarIcon" svgClasses="w-4 h-4 text-primary mr-2" />
                  Allow Schedule Booking
                </label>
                <vs-switch v-model="configScheduleBooking" color="success" />
              </div>

              <div class="setting-row">
                <label class="setting-label">
                  <feather-icon icon="FileTextIcon" svgClasses="w-4 h-4 text-primary mr-2" />
                  Allow Additional Notes
                </label>
                <vs-switch v-model="configAllowNotes" color="success" />
              </div>
            </div>
          </vx-card>
        </div>

        <!-- RIGHT: Vehicle Capabilities -->
        <div class="vx-col w-full md:w-1/2 mb-3" style="padding-left: 0.5rem; padding-right: 0.5rem;">
          <vx-card class="settings-card">
            <div class="card-header-custom">
              <div class="card-title-custom">
                <feather-icon icon="TruckIcon" svgClasses="w-5 h-5 text-primary mr-2" />
                <h4>Vehicle Capabilities</h4>
              </div>
            </div>
            
            <div class="capabilities-section">
              <div v-if="vehicleOptions && vehicleOptions.length > 0" class="vehicle-list">
                <vs-checkbox 
                  v-for="vehicle in vehicleOptions" 
                  :key="vehicle.id"
                  :vs-value="vehicle.id" 
                  v-model="parsedCapabilities"
                  class="vehicle-checkbox-item"
                >
                  {{ vehicle.label }}
                </vs-checkbox>
              </div>
              
              <div v-else class="text-sm text-grey py-2">
                No vehicle options available
              </div>
              
              <span class="text-danger text-sm mt-2 block" v-show="errors.has('edit-device.capabilities')">
                {{ errors.first('edit-device.capabilities') }}
              </span>
            </div>
          </vx-card>
        </div>

        <!-- Payment Modes - Full Width Below -->
        <div class="vx-col w-full mb-3" style="padding-left: 0.5rem; padding-right: 0.5rem;">
          <vx-card class="settings-card">
            <div class="card-header-custom">
              <div class="card-title-custom">
                <feather-icon icon="CreditCardIcon" svgClasses="w-5 h-5 text-primary mr-2" />
                <h4>Payment Modes</h4>
              </div>
            </div>
            
            <div class="payment-section">
              <div class="payment-options">
                <vs-checkbox v-model="paymentModes" vs-value="cash">Cash</vs-checkbox>
                <vs-checkbox v-model="paymentModes" vs-value="card">Card</vs-checkbox>
              </div>
              <span class="text-danger text-sm mt-2 block" v-show="errors.has('edit-device.payment_modes')">
                {{ errors.first('edit-device.payment_modes') }}
              </span>
            </div>
          </vx-card>
        </div>
      </div>

      <!-- Action Footer -->
      <div class="vx-row">
        <div class="vx-col w-full">
          <div class="action-footer">
            <vs-button 
              @click.prevent="updateConfig()" 
              type="filled"
              icon-pack="feather"
              icon="icon-save"
              class="save-button"
            >
              Save Settings
            </vs-button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { _updatePortalSettings } from '@/services/Venue'

import vSelect from 'vue-select'

export default {
  components: {
    'v-select': vSelect,
  },

  props: {
    allowedCapabilities: {
      type: Array,
      default: null
    }
  },

  computed: {
    isPortalModuleReady() {
      // Check if portalModule exists and has required data
      return this.$store.state.portalModule && 
             this.$store.state.portalModule.config &&
             this.$store.state.portalModule.type
    },
    portalConfig: {
      get() {
        // Ensure portalModule exists and has config, return initialized object if not
        if (!this.$store.state.portalModule) {
          return {
            name: false,
            destination: false,
            phone: false,
            schedule_booking: false,
            allow_notes: false,
            payment_modes: []
          }
        }
        if (!this.$store.state.portalModule.config) {
          return {
            name: false,
            destination: false,
            phone: false,
            schedule_booking: false,
            allow_notes: false,
            payment_modes: []
          }
        }
        // Ensure all required properties exist with defaults
        const config = this.$store.state.portalModule.config
        return {
          name: config.name !== undefined ? config.name : false,
          destination: config.destination !== undefined ? config.destination : false,
          phone: config.phone !== undefined ? config.phone : false,
          schedule_booking: config.schedule_booking !== undefined ? config.schedule_booking : false,
          allow_notes: config.allow_notes !== undefined ? config.allow_notes : false,
          request_as: config.request_as || '',
          payment_modes: config.payment_modes || []
        }
      },
      set(value) {
        // This setter is not used directly, but individual properties are updated via their own setters
      },
    },
    // Individual computed properties with setters for each switch
    configName: {
      get() {
        return this.portalConfig.name
      },
      set(value) {
        if (!this.$store.state.portalModule) return
        const currentConfig = this.$store.state.portalModule.config || {}
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          config: {
            ...currentConfig,
            name: value
          }
        })
      }
    },
    configDestination: {
      get() {
        return this.portalConfig.destination
      },
      set(value) {
        if (!this.$store.state.portalModule) return
        const currentConfig = this.$store.state.portalModule.config || {}
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          config: {
            ...currentConfig,
            destination: value
          }
        })
      }
    },
    configPhone: {
      get() {
        return this.portalConfig.phone
      },
      set(value) {
        if (!this.$store.state.portalModule) return
        const currentConfig = this.$store.state.portalModule.config || {}
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          config: {
            ...currentConfig,
            phone: value
          }
        })
      }
    },
    configScheduleBooking: {
      get() {
        return this.portalConfig.schedule_booking
      },
      set(value) {
        if (!this.$store.state.portalModule) return
        const currentConfig = this.$store.state.portalModule.config || {}
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          config: {
            ...currentConfig,
            schedule_booking: value
          }
        })
      }
    },
    configAllowNotes: {
      get() {
        return this.portalConfig.allow_notes
      },
      set(value) {
        if (!this.$store.state.portalModule) return
        const currentConfig = this.$store.state.portalModule.config || {}
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          config: {
            ...currentConfig,
            allow_notes: value
          }
        })
      }
    },

    paymentModes: {
      get() {
        // Ensure portalModule and config exist before accessing
        if (!this.$store.state.portalModule) {
          return []
        }
        if (!this.$store.state.portalModule.config) {
          return []
        }
        
        // Get payment modes from portalConfig, default to empty array for backward compatibility
        if (this.portalConfig && this.portalConfig.payment_modes && Array.isArray(this.portalConfig.payment_modes)) {
          return this.portalConfig.payment_modes
        }
        // Return empty array if not exists (backward compatibility for existing portals)
        return []
      },
      set(value) {
        // Ensure portalModule exists before updating
        if (!this.$store.state.portalModule) {
          return
        }
        const currentConfig = this.$store.state.portalModule.config || {}
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          config: {
            ...currentConfig,
            payment_modes: Array.isArray(value) ? value : []
          }
        })
      },
    },

    portal: {
      get() {
        // Ensure portalModule exists, return empty object if not
        if (!this.$store.state.portalModule) {
          return {}
        }
        return this.$store.state.portalModule
      },
    },

    parsedCapabilities: {
      get() {
        try {
          const parsed = JSON.parse(this.portal.capabilities || '[]')
          // Ensure we return an array of IDs (numbers)
          // Handle both array of IDs and array of objects
          if (Array.isArray(parsed)) {
            return parsed.map(item => {
              // If item is already a number (ID), return it
              if (typeof item === 'number') {
                return item
              }
              // If item is an object with id property, return the id
              if (item && typeof item === 'object' && item.id !== undefined) {
                return item.id
              }
              return item
            }).filter(id => id !== null && id !== undefined)
          }
          return []
        } catch (error) {
          console.error('Failed to parse capabilities:', error)
          return []
        }
      },

      set(value) {
        // Ensure value is an array of IDs before stringifying
        const normalizedValue = Array.isArray(value) 
          ? value.filter(id => id !== null && id !== undefined)
          : []
        // Update capabilities using dispatch action instead of direct mutation
        if (!this.$store.state.portalModule) return
        this.$store.dispatch('portalModule/updatePortal', {
          ...this.portal,
          capabilities: JSON.stringify(normalizedValue)
        })
      },
    },

    vehicleOptions() {
      // Use allowed_capabilities from prop if available, otherwise fallback to hardcoded values
      if (this.allowedCapabilities && Array.isArray(this.allowedCapabilities) && this.allowedCapabilities.length > 0) {
        // Normalize API data to ensure all items have 'label' and 'id' properties
        return this.allowedCapabilities.map(option => {
          // Extract ID - prioritize id, then Featureid
          const id = option.id !== undefined ? option.id : 
                     (option.Featureid !== undefined ? option.Featureid : null)
          
          // Extract label - prioritize label, then name, then shortCode
          const label = option.label || option.name || option.shortCode || `Capability ${id}`
          
          return {
            label: label,
            id: id !== null ? Number(id) : null // Ensure ID is a number for proper comparison
          }
        }).filter(opt => opt.id !== null) // Filter out options without valid IDs
      }
      // Return empty array if no allowed capabilities
      return []
    },
  },

  data() {
    return {
      selectedCapabilities: [], // local array for v-select binding
      isMounted: false,
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.isMounted = true;
    });
  },

  watch: {
    paymentModes: {
      handler() {
        // Clear validation error when payment modes change
        if (this.paymentModes && this.paymentModes.length > 0) {
          this.$validator.errors.remove('edit-device.payment_modes')
        }
      },
      deep: true
    }
  },

  methods: {
    validatePaymentModes() {
      if (!this.paymentModes || this.paymentModes.length === 0) {
        this.$validator.errors.add({
          field: 'edit-device.payment_modes',
          msg: 'At least one payment mode must be selected'
        })
        return false
      } else {
        this.$validator.errors.remove('edit-device.payment_modes')
        return true
      }
    },
    
    updateConfig() {
      // Ensure backward compatibility: always include payment_modes in config
      const configToSave = { ...this.portalConfig }
      
      // Ensure payment_modes exists and is an array
      if (!configToSave.payment_modes) {
        configToSave.payment_modes = []
      } else if (!Array.isArray(configToSave.payment_modes)) {
        configToSave.payment_modes = []
      }

      // Update portal config using dispatch action instead of direct mutation
      this.$store.dispatch('portalModule/updatePortal', {
        ...this.portal,
        config: configToSave
      })

      // Validate payment modes first
      const paymentModesValid = this.validatePaymentModes()

      this.$validator.validateAll('edit-device').then((result) => {
        if (result && paymentModesValid) {
          this.$vs.loading()

          _updatePortalSettings(
            this.portal.venue_id,

            this.portal.id,

            this.portal
          ).then((response) => {
            this.$vs.loading.close()

            this.$emit('updateConfigParent', response)

            this.$vs.notify({
              title: response.data.responseTitle,

              text: response.data.responseMessage,

              iconPack: 'feather',

              icon: 'icon-check-circle',

              color: 'success',
            })
          })
        } else {
          // if (!paymentModesValid) {
          //   this.$vs.notify({
          //     title: 'Validation Error',
          //     text: 'At least one payment mode must be selected',
          //     iconPack: 'feather',
          //     icon: 'icon-alert-circle',
          //     color: 'danger',
          //   })
          // }
        }
      })
    },
  },
}
</script>



<style scoped>
.portal-settings-wrapper {
  padding: 1rem;
}

.portal-settings-form {
  max-width: 100%;
}

/* Settings Card */
.settings-card {
  height: 100%;
}

.settings-card >>> .vx-card__body {
  padding: 1rem;
}

.settings-card >>> .vx-card__header {
  display: none;
}

.card-header-custom {
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid #dae1e7;
  margin-bottom: 1rem;
}

.card-header-custom {
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid #dae1e7;
}

.card-title-custom {
  display: flex;
  align-items: center;
}

.card-title-custom h4 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #22292f;
}

/* Settings List */
.settings-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.setting-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #22292f;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Payment Section */
.payment-section {
  padding-top: 0.5rem;
}

.payment-options {
  display: flex;
  gap: 1.5rem;
}

/* Capabilities Section */
.capabilities-section {
  display: flex;
  flex-direction: column;
}

.capabilities-section .vs-input--label {
  display: flex;
  align-items: center;
}

.vehicle-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.vehicle-checkbox-item {
  margin: 0;
}

/* Action Footer */
.action-footer {
  display: flex;
  justify-content: center;
  padding: 0.75rem 0 0;
  margin-top: 0.5rem;
}

.save-button {
  padding: 0.625rem 1.5rem;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Tooltip Override */
.vx-tooltip-content {
  z-index: 9999 !important;
}

.modal-container {
  overflow: visible !important;
}

/* Responsive Adjustments */
@media (max-width: 767px) {
  .portal-settings-wrapper {
    padding: 0.75rem;
  }

  .settings-card >>> .vx-card__body {
    padding: 0.75rem;
  }

  .setting-row {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .payment-options {
    flex-direction: column;
    gap: 0.5rem;
  }

  .save-button {
    width: 100%;
  }
}

/* Print Styles */
@media print {
  .action-footer {
    display: none;
  }
}
</style>
